/**
 * Wpływ opcji "Hak holowniczy" (order.options) na pola sekcji 16.4/17.4
 * (masa zestawu), 18.3/18.4 (masa holowana) i 19 (masa statyczna na
 * sprzęgle) w Certificate of Conformity.
 *
 * Hak jest pojedynczą opcją zamówienia (nie należy do żadnej grupy - w
 * przeciwieństwie do np. rozstawu osi), rozpoznawaną po stałym `_id` i
 * uznawaną za aktywną, gdy jest `checked` LUB `required` (patrz
 * `isOrderOptionActiveById` w order-options.ts - różne źródła danych w
 * systemie zamówień oznaczają to raz jednym polem, raz drugim).
 *
 * Reguła biznesowa (wspólna dla Iveco i Mercedes Benz Tourist Line
 * HighDeck, z jedną różnicą dla zwykłego Mercedes Benz Tourist Line -
 * inna kwota zwiększenia masy zestawu):
 * - 18.3 (masa holowana - przyczepa centralna) = stała kwota: 2000 kg
 *   (Iveco, MB HighDeck) albo 1500 kg (MB Tourist Line),
 * - 16.4 (maks. masa zestawu) = 16.1 (maks. masa całkowita pojazdu,
 *   {{maxLadenMass}}, zawsze liczba - masa pojazdu ze zlecenia) powiększona
 *   o wartość 18.3 powyżej,
 * - 17.4 (przewidywana maks. masa zestawu) = ta sama wartość co 16.4,
 * - 18.4 (masa holowana - przyczepa niehamowana) = stałe 750 kg,
 * - 19 (maks. masa statyczna na sprzęgle) = stałe 100 kg.
 * Gdy hak NIE jest aktywny - wszystkie te pola mają wartość "N/A" (patrz
 * wywołanie w computeVariableValue w komponencie).
 *
 * @module tow-hook
 */

import { VehicleLine } from "./vehicle-line";
import { isOrderOptionActiveById, type OrderOption } from "./order-options";

/** `_id` opcji zamówienia oznaczającej hak holowniczy */
export const TOW_HOOK_OPTION_ID = "60d56a718bbe5f76208c6099";

/**
 * O ile hak holowniczy zwiększa masę zestawu (16.4/17.4) ponad masę
 * całkowitą pojazdu (16.1) - ta sama kwota trafia też wprost do pola 18.3.
 */
const TOW_HOOK_MASS_INCREMENT: Partial<Record<VehicleLine, number>> = {
  [VehicleLine.IvecoTouristLine]: 2000,
  [VehicleLine.MercedesTouristLineHighDeck]: 2000,
  [VehicleLine.MercedesTouristLine]: 1500,
};

/** Wartości pól zależnych od haka holowniczego, gdy hak jest aktywny */
export interface TowHookMassValues {
  /** 18.3 - masa holowana (przyczepa centralna) */
  towableCentreAxle: string;
  /** 16.4 - maks. dopuszczalna masa zestawu */
  maxMassCombination: string;
  /** 17.4 - przewidywana maks. masa zestawu */
  intendedMaxMassCombination: string;
  /** 18.4 - masa holowana (przyczepa niehamowana) */
  towableUnbraked: string;
  /** 19 - maks. dopuszczalna masa statyczna na sprzęgle */
  staticMassCoupling: string;
}

/**
 * Czy w opcjach zamówienia jest aktywny (checked lub required) hak holowniczy.
 *
 * @param options - Tablica opcji zamówienia (order.options)
 */
export function isTowHookSelected(
  options: OrderOption[] | undefined | null,
): boolean {
  return isOrderOptionActiveById(options, TOW_HOOK_OPTION_ID);
}

/**
 * Wyznacza wartości pól 16.4/17.4/18.3/18.4/19 dla zabudowy z aktywnym
 * hakiem holowniczym. Zwraca null, gdy zabudowa nie ma zarejestrowanej
 * kwoty zwiększenia (`TOW_HOOK_MASS_INCREMENT`) albo masa całkowita pojazdu
 * (16.1) nie jest liczbą - w obu przypadkach nie da się policzyć wyniku bez
 * zgadywania.
 *
 * @param line - Zabudowa (VehicleLine) albo null, gdy nierozpoznana
 * @param maxLadenMass - Masa całkowita pojazdu (16.1, {{maxLadenMass}} - order.vehicleWeight.weight)
 */
export function resolveTowHookMassValues(
  line: VehicleLine | null,
  maxLadenMass: number | string | undefined | null,
): TowHookMassValues | null {
  if (!line) return null;
  const increment = TOW_HOOK_MASS_INCREMENT[line];
  if (increment === undefined) return null;

  const baseMass = Number(maxLadenMass);
  if (!Number.isFinite(baseMass)) return null;

  const combinationMass = `${baseMass + increment} kg`;

  return {
    towableCentreAxle: `${increment} kg`,
    maxMassCombination: combinationMass,
    intendedMaxMassCombination: combinationMass,
    towableUnbraked: "750 kg",
    staticMassCoupling: "100 kg",
  };
}
